(() => {
  'use strict';

  const theme = window.theme || {};
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let activeDrawerTrigger = null;

  function formatMoney(cents, format = theme.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(number)) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };
    const match = format.match(placeholder);
    if (!match) return formatWithDelimiters(value);
    let output;
    switch (match[1]) {
      case 'amount_no_decimals': output = formatWithDelimiters(value, 0); break;
      case 'amount_with_comma_separator': output = formatWithDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': output = formatWithDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': output = formatWithDelimiters(value, 2, "'", '.'); break;
      default: output = formatWithDelimiters(value, 2);
    }
    return format.replace(placeholder, output);
  }

  function lockBody(locked) {
    document.body.classList.toggle('is-locked', locked);
  }

  function focusFirst(container) {
    const focusable = $('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
    focusable?.focus({ preventScroll: true });
  }

  function closeAllDrawers(except = null) {
    $$('.mobile-menu.is-open, .search-drawer.is-open, .cart-drawer.is-open, .collection-filters.is-open').forEach((drawer) => {
      if (drawer === except) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    $$('[data-open-menu], [data-open-search], [data-open-cart], [data-open-filter]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    if (!except) {
      lockBody(false);
      const trigger = activeDrawerTrigger;
      activeDrawerTrigger = null;
      window.setTimeout(() => trigger?.focus({ preventScroll: true }), 0);
    }
  }

  function openDrawer(drawer, trigger) {
    if (!drawer) return;
    closeAllDrawers(drawer);
    activeDrawerTrigger = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    trigger?.setAttribute('aria-expanded', 'true');
    lockBody(true);
    window.setTimeout(() => focusFirst(drawer.querySelector('[class$="__panel"]') || drawer), 250);
  }

  function closeDrawer(drawer, triggerSelector) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    $$(triggerSelector).forEach((button) => button.setAttribute('aria-expanded', 'false'));
    if (!document.querySelector('.is-open:is(.mobile-menu,.search-drawer,.cart-drawer,.collection-filters)')) {
      lockBody(false);
      const trigger = activeDrawerTrigger;
      activeDrawerTrigger = null;
      window.setTimeout(() => trigger?.focus({ preventScroll: true }), 0);
    }
  }

  function initializeDrawers() {
    document.addEventListener('click', (event) => {
      const openMenu = event.target.closest('[data-open-menu]');
      const closeMenu = event.target.closest('[data-close-menu]');
      const openSearch = event.target.closest('[data-open-search]');
      const closeSearch = event.target.closest('[data-close-search]');
      const openCart = event.target.closest('[data-open-cart]');
      const closeCart = event.target.closest('[data-close-cart]');
      const openFilter = event.target.closest('[data-open-filter]');
      const closeFilter = event.target.closest('[data-close-filter]');

      if (openMenu) openDrawer($('#MobileMenu'), openMenu);
      if (closeMenu) closeDrawer($('#MobileMenu'), '[data-open-menu]');
      if (openSearch) openDrawer($('#SearchDrawer'), openSearch);
      if (closeSearch) closeDrawer($('#SearchDrawer'), '[data-open-search]');
      if (openCart) openDrawer($('#CartDrawer'), openCart);
      if (closeCart) closeDrawer($('#CartDrawer'), '[data-open-cart]');
      if (openFilter) openDrawer($('#CollectionFilters'), openFilter);
      if (closeFilter) closeDrawer($('#CollectionFilters'), '[data-open-filter]');
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeAllDrawers();
        return;
      }
      if (event.key !== 'Tab') return;
      const drawer = document.querySelector('.is-open:is(.mobile-menu,.search-drawer,.cart-drawer,.collection-filters)');
      if (!drawer) return;
      const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', drawer)
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function initializeHeader(root = document) {
    const header = $('[data-site-header]', root) || $('[data-site-header]');
    if (!header || header.dataset.initialized === 'true') return;
    header.dataset.initialized = 'true';
    const announcement = $('.announcement-bar');
    const setAnnouncementHeight = () => {
      document.documentElement.style.setProperty('--announcement-height', `${announcement?.offsetHeight || 0}px`);
    };
    const update = () => {
      const scrolled = window.scrollY > 20;
      header.classList.toggle('is-scrolled', scrolled);
    };
    setAnnouncementHeight();
    update();
    window.addEventListener('resize', setAnnouncementHeight, { passive: true });
    window.addEventListener('scroll', update, { passive: true });
  }

  function showToast(message, type = 'success') {
    const region = $('[data-toast-region]');
    if (!region) return;
    const toast = document.createElement('div');
    toast.className = `toast${type === 'error' ? ' is-error' : ''}`;
    toast.textContent = message;
    region.appendChild(toast);
    window.setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(10px)';
      window.setTimeout(() => toast.remove(), 250);
    }, 3200);
  }

  async function getCart() {
    const response = await fetch(`${theme.routes?.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  function updateCartCount(cart) {
    $$('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('is-empty', cart.item_count === 0);
    });
  }

  async function refreshCartDrawer(open = false) {
    const route = theme.routes?.cart || '/cart';
    const response = await fetch(`${route}?section_id=cart-drawer`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Unable to refresh cart');
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('#CartDrawer', parsed);
    const current = $('#CartDrawer');
    if (fresh && current) current.replaceWith(fresh);
    const cart = await getCart();
    updateCartCount(cart);
    if (open) openDrawer($('#CartDrawer'), $('[data-open-cart]'));
    return cart;
  }

  function initializeAjaxCart() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-product-form]');
      if (!form) return;
      event.preventDefault();
      const submit = $('[data-add-to-cart]', form);
      if (submit?.disabled) return;
      submit?.classList.add('is-loading');
      submit?.setAttribute('aria-disabled', 'true');
      try {
        const response = await fetch(theme.routes?.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || theme.strings?.cartError || 'Unable to add item');
        await refreshCartDrawer(theme.enableCartDrawer !== false);
        showToast(theme.strings?.cartAdded || 'Added to cart');
      } catch (error) {
        showToast(error.message || theme.strings?.cartError || 'Unable to add item', 'error');
      } finally {
        submit?.classList.remove('is-loading');
        submit?.removeAttribute('aria-disabled');
      }
    });

    document.addEventListener('change', async (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      try {
        await fetch(theme.routes?.cartChange || '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: input.dataset.lineKey, quantity: Number(input.value) })
        });
        await refreshCartDrawer(true);
      } catch (error) {
        showToast(theme.strings?.cartError || 'Unable to update cart', 'error');
      }
    });

    document.addEventListener('click', async (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      try {
        await fetch(theme.routes?.cartChange || '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: remove.dataset.lineKey, quantity: 0 })
        });
        await refreshCartDrawer(true);
      } catch (error) {
        showToast(theme.strings?.cartError || 'Unable to update cart', 'error');
      }
    });

    let noteTimer;
    document.addEventListener('input', (event) => {
      const note = event.target.closest('[data-cart-note]');
      if (!note) return;
      window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(() => {
        fetch(theme.routes?.cartUpdate || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: note.value })
        }).catch(() => {});
      }, 450);
    });
  }

  function initializeQuantityButtons() {
    document.addEventListener('click', (event) => {
      const minus = event.target.closest('[data-quantity-minus]');
      const plus = event.target.closest('[data-quantity-plus]');
      if (!minus && !plus) return;
      const wrapper = event.target.closest('.quantity-selector');
      const input = $('[data-quantity-input]', wrapper);
      if (!input) return;
      const current = Number(input.value) || 1;
      const minimum = Number(input.min || 0);
      input.value = String(Math.max(minimum, current + (plus ? 1 : -1)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function initializePredictiveSearch(root = document) {
    const form = $('[data-predictive-search-form]', root) || $('[data-predictive-search-form]');
    if (!form || form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';
    const input = $('input[type="search"]', form);
    const results = $('[data-predictive-search-results]');
    let timer;
    let controller;

    const search = async () => {
      const term = input.value.trim();
      if (term.length < 2) {
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      controller?.abort();
      controller = new AbortController();
      const base = theme.routes?.predictiveSearch || '/search/suggest';
      const url = `${base}?q=${encodeURIComponent(term)}&resources[type]=product,query,page,article&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Search failed');
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const content = $('.predictive-search__inner', parsed);
        results.innerHTML = content ? content.outerHTML : '';
        input.setAttribute('aria-expanded', content ? 'true' : 'false');
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '';
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(search, 180);
    });
  }

  function initializeReveals(root = document) {
    const elements = $$('[data-reveal]:not(.is-visible)', root);
    if (!elements.length) return;
    if (reducedMotion || document.body.dataset.animations === 'false' || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .08, rootMargin: '0px 0px -6% 0px' });
    elements.forEach((element, index) => {
      element.style.transitionDelay = `${Math.min(index % 5, 4) * 55}ms`;
      observer.observe(element);
    });
  }

  function initializeTilt(root = document) {
    if (reducedMotion || document.body.dataset.animations === 'false' || window.matchMedia('(pointer: coarse)').matches) return;
    $$('[data-tilt-card]:not([data-tilt-ready])', root).forEach((card) => {
      card.dataset.tiltReady = 'true';
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        card.style.transform = `perspective(900px) rotateX(${-y * 4}deg) rotateY(${x * 5}deg) translateZ(0)`;
      });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });
  }

  function initializeDragScroll(root = document) {
    $$('[data-drag-scroll]:not([data-drag-ready])', root).forEach((rail) => {
      rail.dataset.dragReady = 'true';
      let active = false;
      let startX = 0;
      let startScroll = 0;
      rail.addEventListener('pointerdown', (event) => {
        active = true;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        rail.classList.add('is-dragging');
        rail.setPointerCapture?.(event.pointerId);
      });
      rail.addEventListener('pointermove', (event) => {
        if (!active) return;
        rail.scrollLeft = startScroll - (event.clientX - startX);
      });
      const stop = () => { active = false; rail.classList.remove('is-dragging'); };
      rail.addEventListener('pointerup', stop);
      rail.addEventListener('pointercancel', stop);
      rail.addEventListener('pointerleave', stop);
    });
  }

  function initializeProduct(root = document) {
    $$('[data-product-section]:not([data-product-ready])', root).forEach((section) => {
      section.dataset.productReady = 'true';
      const variantsNode = $('[data-product-variants]', section);
      if (!variantsNode) return;
      let variants;
      try { variants = JSON.parse(variantsNode.textContent); } catch (_) { return; }
      const form = $('[data-product-form]', section);
      const idInput = $('[data-variant-id]', form);
      const submit = $('[data-add-to-cart]', form);
      const submitText = $('[data-add-to-cart-text]', form);
      const price = $('[data-product-price]', section);

      const updateVariant = () => {
        const selected = $$('[data-option-position]:checked', form)
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
          .map((input) => input.value);
        const variant = variants.find((candidate) => candidate.options.every((option, index) => option === selected[index]));
        if (!variant) {
          if (submit) submit.disabled = true;
          if (submitText) submitText.textContent = theme.strings?.unavailable || 'Unavailable';
          return;
        }
        idInput.value = variant.id;
        if (submit) submit.disabled = !variant.available;
        if (submitText) submitText.textContent = variant.available ? (theme.strings?.addToCart || 'Add to cart') : (theme.strings?.soldOut || 'Sold out');
        if (price) {
          const compare = variant.compare_at_price > variant.price ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : '';
          price.innerHTML = `<div class="price"><span class="price__sale">${formatMoney(variant.price)}</span>${compare}</div>`;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.featured_media?.id) activateMedia(section, variant.featured_media.id);
      };

      form?.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-position]')) updateVariant();
      });
    });
  }

  function activateMedia(section, mediaId) {
    $$('[data-media-id]', section).forEach((media) => media.classList.toggle('is-active', String(media.dataset.mediaId) === String(mediaId)));
    $$('[data-media-target]', section).forEach((thumb) => {
      const active = String(thumb.dataset.mediaTarget) === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  function initializeMediaGallery() {
    document.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-media-target]');
      if (!thumb) return;
      const section = thumb.closest('[data-product-section]');
      activateMedia(section, thumb.dataset.mediaTarget);
    });
  }

  function initializeRecommendations(root = document) {
    $$('[data-product-recommendations]:not([data-loaded])', root).forEach(async (section) => {
      section.dataset.loaded = 'true';
      if (!section.dataset.url) return;
      try {
        const response = await fetch(section.dataset.url);
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const fresh = $('[data-product-recommendations]', parsed);
        if (fresh?.innerHTML.trim()) {
          section.innerHTML = fresh.innerHTML;
          initializeReveals(section);
          initializeTilt(section);
        }
      } catch (_) {}
    });
  }

  function initializeCollection() {
    document.addEventListener('change', (event) => {
      const select = event.target.closest('[data-sort-select]');
      if (!select) return;
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.href = url.toString();
    });
  }

  function initializeCopyLink() {
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-copy-link]');
      if (!button) return;
      try {
        await navigator.clipboard.writeText(window.location.href);
        showToast('Link copied');
      } catch (_) {
        showToast('Could not copy link', 'error');
      }
    });
  }

  function initializeLocalization(root = document) {
    $$('[data-localization-select]:not([data-localization-ready])', root).forEach((select) => {
      select.dataset.localizationReady = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function initializeCustomerForms() {
    if (window.location.hash === '#recover') {
      $('[data-recover-password]')?.removeAttribute('hidden');
      $('[data-login-form]')?.setAttribute('hidden', '');
    }
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-show-recover]')) {
        $('[data-recover-password]')?.removeAttribute('hidden');
        $('[data-login-form]')?.setAttribute('hidden', '');
      }
      if (event.target.closest('[data-hide-recover]')) {
        $('[data-recover-password]')?.setAttribute('hidden', '');
        $('[data-login-form]')?.removeAttribute('hidden');
      }
      const deleteButton = event.target.closest('[data-address-delete]');
      if (deleteButton && window.confirm(deleteButton.dataset.confirmMessage || 'Delete this address?')) {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = deleteButton.dataset.target;
        const method = document.createElement('input');
        method.type = 'hidden';
        method.name = '_method';
        method.value = 'delete';
        form.appendChild(method);
        document.body.appendChild(form);
        form.submit();
      }
    });
  }

  function initializeCustomCursor() {
    if (!document.body.classList.contains('has-custom-cursor')) return;
    const dot = $('[data-cursor-dot]');
    const ring = $('[data-cursor-ring]');
    if (!dot || !ring || window.matchMedia('(pointer: coarse)').matches || reducedMotion) return;
    document.body.classList.add('cursor-enabled');
    let dotX = 0, dotY = 0, ringX = 0, ringY = 0;
    document.addEventListener('pointermove', (event) => {
      dotX = event.clientX;
      dotY = event.clientY;
      document.body.classList.add('cursor-visible');
      dot.style.transform = `translate3d(${dotX - 3.5}px, ${dotY - 3.5}px, 0)`;
      document.body.classList.toggle('cursor-hover', Boolean(event.target.closest('a, button, summary, input, select, textarea, [data-tilt-card]')));
    }, { passive: true });
    document.addEventListener('pointerleave', () => document.body.classList.remove('cursor-visible'));
    const animate = () => {
      ringX += (dotX - ringX) * .16;
      ringY += (dotY - ringY) * .16;
      ring.style.transform = `translate3d(${ringX - ring.offsetWidth / 2}px, ${ringY - ring.offsetHeight / 2}px, 0)`;
      requestAnimationFrame(animate);
    };
    animate();
  }



  function initializeCinematicMotion(root = document) {
    if (reducedMotion || document.body.dataset.animations === 'false') return;
    const heroes = $$('[data-particle-hero]:not([data-cinematic-ready])', root);
    if (!heroes.length) return;
    heroes.forEach((hero) => { hero.dataset.cinematicReady = 'true'; });
    let ticking = false;
    const update = () => {
      ticking = false;
      heroes.forEach((hero) => {
        const rect = hero.getBoundingClientRect();
        const progress = clamp((0 - rect.top) / Math.max(1, rect.height), 0, 1);
        const title = hero.querySelector('.particle-hero__title');
        const ring = hero.querySelector('.particle-hero__ring-scene');
        const stamp = hero.querySelector('.particle-hero__edition-stamp');
        const lab = hero.querySelector('.particle-hero__lab');
        if (title) title.style.transform = `translate3d(0, ${progress * -42}px, 0) scale(${1 - progress * 0.035})`;
        if (ring) ring.style.transform = `translateY(calc(-50% + ${progress * 85}px)) rotateZ(${progress * 8}deg)`;
        if (stamp) stamp.style.transform = `translate(-50%, calc(-50% + ${progress * 110}px)) scale(${1 + progress * 0.08})`;
        if (lab) lab.style.transform = `translate3d(0, ${48 - progress * 90}px, 0) rotate(${progress * -2}deg)`;
      });
    };
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    requestUpdate();
  }

  function initializeAll(root = document) {
    initializeHeader(root);
    initializePredictiveSearch(root);
    initializeReveals(root);
    initializeTilt(root);
    initializeDragScroll(root);
    initializeCinematicMotion(root);
    initializeProduct(root);
    initializeRecommendations(root);
    initializeLocalization(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initializeDrawers();
    initializeAjaxCart();
    initializeQuantityButtons();
    initializeMediaGallery();
    initializeCollection();
    initializeCopyLink();
    initializeCustomerForms();
    initializeCustomCursor();
    initializeAll();
  });

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  window.MediaKingsTheme = { initialize: initializeAll, refreshCartDrawer, formatMoney };
})();
